import {
	ThemeOptions,
	ThemeProvider,
	unstable_createMuiStrictModeTheme as createMuiTheme
} from '@material-ui/core';
import {
	ConfigCatProvider,
	createConsoleLogger,
	createFlagOverridesFromMap,
	IReactAutoPollOptions,
	OverrideBehaviour,
	PollingMode
} from 'configcat-react';
import { ConnectedRouter, RouterLocation } from 'connected-react-router';
import { createRef, RefObject, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { config } from '../config';
import { useFullBrandName, useOpcoTheme } from '../hooks';
import '../i18n/i18nInit';
import { errorInterceptor } from '../interceptors/error.interceptor';
import { Opco } from '../models';
import {
	AppState,
	AppThunkDispatch,
	fetchProfile,
	history,
	ProfileAction
} from '../redux';
import { httpClient } from '../services';
import { ScrollReset } from '../utils';
import { IdentityProvider } from './indentity-provider.component';
import { PageFooter, PageHeader, SnackbarProvider } from './molecules';
import { Routing } from './routing.component';
import { TelemetryProviderWrapper } from './telemetry-provider.component';

import './App.scss';
import { useTracker } from '../hooks/useTracker';
import { i18nInit } from '../i18n/i18nInit';

export interface IAppProps {
	location: RouterLocation<unknown>;
	fetchProfile: () => void;
}

declare global {
	interface Window {
		Buffer: any;
	}
}

window.Buffer = window.Buffer || require('buffer').Buffer;

function AppView(props: IAppProps) {
	const { location, fetchProfile } = props;
	const tracker = useTracker();
	const opcoThemeOptions: ThemeOptions = useOpcoTheme();
	const hideFooterPages: string[] = ['/map', '/project', '/comparison'];
	const fullBrandName: string = useFullBrandName();
	const pageWrapperRef: RefObject<HTMLDivElement> =
		createRef<HTMLDivElement>();

	const hideFooter: boolean = useMemo<boolean>(() => {
		for (let page of hideFooterPages) {
			if (location.pathname.startsWith(page)) {
				return true;
			}
		}
		return false;
	}, [location]);

	useEffect(() => {
		tracker.listen();
	}, []);

	const [i18nLoaded, seti18nLoaded] = useState(false);

	const handleSetInitFinished = () => {
		seti18nLoaded(true);
		return undefined;
	};
	useEffect(() => {
		i18nInit(handleSetInitFinished);
	}, []);

	useEffect(() => {
		errorInterceptor(httpClient);
		setTheme();
		fetchProfile();
		window.addEventListener(ScrollReset, scrollTop);

		return () => {
			window.removeEventListener(ScrollReset, scrollTop);
		};
	}, []);

	useEffect(() => {
		document.title = fullBrandName;
	}, [fullBrandName]);

	const theme = createMuiTheme(opcoThemeOptions);

	const configCatOptions: IReactAutoPollOptions = {
		pollIntervalSeconds: 300,
		requestTimeoutMs: 5000,
		maxInitWaitTimeSeconds: 0.5,
		logger: createConsoleLogger(-1),
		flagOverrides: createFlagOverridesFromMap(
			{},
			OverrideBehaviour.LocalOverRemote
		)
	};

	function setTheme(): void {
		if (window.location.href.includes('plantyn.com'))
			window.localStorage.setItem(
				config.selectedOpcoLocalstorageKey,
				`${Opco.Plantyn}`
			);
	}

	function scrollTop(): void {
		const element: HTMLDivElement | null = pageWrapperRef.current;
		if (!element) return;

		element.scrollTo({ top: 0, behavior: 'smooth' });
	}

	if(!i18nLoaded) return null

	return (
		<div id="app" className="App">
			<TelemetryProviderWrapper>
				<IdentityProvider>
					<ConfigCatProvider
						sdkKey={config.configcat.featuresKey}
						pollingMode={PollingMode.AutoPoll}
						options={configCatOptions}
					>
						<ThemeProvider theme={theme}>
							<SnackbarProvider>
								<ConnectedRouter history={history}>
									<PageHeader />
									<div
										ref={pageWrapperRef}
										className="page-wrapper"
									>
										<Routing />
									</div>
									{!hideFooter && <PageFooter />}
								</ConnectedRouter>
							</SnackbarProvider>
						</ThemeProvider>
					</ConfigCatProvider>
				</IdentityProvider>
			</TelemetryProviderWrapper>
		</div>
	);
}

function mapStateToProps(state: AppState) {
	return {
		location: state.router.location
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ProfileAction>) {
	return {
		fetchProfile: () => dispatch(fetchProfile())
	};
}

export const App = connect(mapStateToProps, mapDispatchToProps)(AppView);
